import React, { useState, useEffect, useContext } from "react";
import {
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  CircularProgress
} from "@material-ui/core";
import {
  Add,
  Visibility,
  Send,
  Close
} from "@material-ui/icons";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import MainContainer from "../../components/MainContainer";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
  },
  chatContainer: {
    display: "flex",
    flexDirection: "column",
    height: "60vh",
    overflowY: "auto",
    padding: theme.spacing(2),
    background: "#f5f5f5",
    borderRadius: 8,
    marginBottom: theme.spacing(2),
  },
  messageBubble: {
    maxWidth: "70%",
    padding: theme.spacing(1.5),
    borderRadius: 12,
    marginBottom: theme.spacing(1),
  },
  myMessage: {
    alignSelf: "flex-end",
    background: "#dcf8c6",
  },
  adminMessage: {
    alignSelf: "flex-start",
    background: "#fff",
    border: "1px solid #e0e0e0",
  },
  messageInput: {
    display: "flex",
    gap: theme.spacing(1),
  },
}));

const priorityColors = {
  low: "default",
  medium: "primary",
  high: "secondary",
  urgent: "secondary",
};

const priorityLabels = {
  low: "Baixa",
  medium: "Média",
  high: "Alta",
  urgent: "Urgente",
};

const statusLabels = {
  open: "Aberto",
  in_progress: "Em Andamento",
  resolved: "Resolvido",
  closed: "Fechado",
};

const statusColors = {
  open: "#4caf50",
  in_progress: "#2196f3",
  resolved: "#ff9800",
  closed: "#9e9e9e",
};

const categoryLabels = {
  bug: "Bug",
  question: "Dúvida",
  suggestion: "Sugestão",
  financial: "Financeiro",
  technical: "Técnico",
};

const Support = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNewModal, setOpenNewModal] = useState(false);
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [newTicket, setNewTicket] = useState({
    subject: "",
    description: "",
    priority: "medium",
    category: "technical",
  });

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/support-tickets");
      setTickets(data.tickets);
    } catch (err) {
      toast.error("Erro ao carregar tickets");
    }
    setLoading(false);
  };

  const handleCreateTicket = async () => {
    if (!newTicket.subject || !newTicket.description) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }
    try {
      await api.post("/support-tickets", newTicket);
      toast.success("Ticket aberto com sucesso!");
      setOpenNewModal(false);
      setNewTicket({ subject: "", description: "", priority: "medium", category: "technical" });
      loadTickets();
    } catch (err) {
      toast.error("Erro ao criar ticket");
    }
  };

  const handleOpenDetail = async (ticketId) => {
    try {
      const { data } = await api.get(`/support-tickets/${ticketId}`);
      setSelectedTicket(data);
      setOpenDetailModal(true);
    } catch (err) {
      toast.error("Erro ao carregar ticket");
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    try {
      await api.post(`/support-tickets/${selectedTicket.id}/messages`, { body: newMessage });
      setNewMessage("");
      // Recarregar ticket
      const { data } = await api.get(`/support-tickets/${selectedTicket.id}`);
      setSelectedTicket(data);
    } catch (err) {
      toast.error("Erro ao enviar mensagem");
    }
  };

  const handleCloseTicket = async () => {
    try {
      await api.put(`/support-tickets/${selectedTicket.id}/close`);
      toast.success("Ticket fechado");
      setOpenDetailModal(false);
      loadTickets();
    } catch (err) {
      toast.error("Erro ao fechar ticket");
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Suporte</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => setOpenNewModal(true)}
          >
            Novo Ticket
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : tickets.length === 0 ? (
          <Box display="flex" justifyContent="center" p={4}>
            <Typography color="textSecondary">
              Nenhum ticket aberto. Clique em "Novo Ticket" para abrir um.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Assunto</TableCell>
                  <TableCell>Categoria</TableCell>
                  <TableCell>Prioridade</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tickets.map((ticket) => (
                  <TableRow key={ticket.id}>
                    <TableCell>#{ticket.id}</TableCell>
                    <TableCell>{ticket.subject}</TableCell>
                    <TableCell>{categoryLabels[ticket.category] || ticket.category}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={priorityLabels[ticket.priority]}
                        color={priorityColors[ticket.priority]}
                        style={ticket.priority === "urgent" ? { background: "#f44336", color: "#fff" } : {}}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={statusLabels[ticket.status]}
                        style={{ background: statusColors[ticket.status], color: "#fff" }}
                      />
                    </TableCell>
                    <TableCell>
                      {format(parseISO(ticket.createdAt), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleOpenDetail(ticket.id)}>
                        <Visibility />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Modal Novo Ticket */}
      <Dialog open={openNewModal} onClose={() => setOpenNewModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Abrir Novo Ticket</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Assunto"
                variant="outlined"
                margin="dense"
                value={newTicket.subject}
                onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                select
                label="Categoria"
                variant="outlined"
                margin="dense"
                value={newTicket.category}
                onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })}
              >
                <MenuItem value="bug">Bug</MenuItem>
                <MenuItem value="question">Dúvida</MenuItem>
                <MenuItem value="suggestion">Sugestão</MenuItem>
                <MenuItem value="financial">Financeiro</MenuItem>
                <MenuItem value="technical">Técnico</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                select
                label="Prioridade"
                variant="outlined"
                margin="dense"
                value={newTicket.priority}
                onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
              >
                <MenuItem value="low">Baixa</MenuItem>
                <MenuItem value="medium">Média</MenuItem>
                <MenuItem value="high">Alta</MenuItem>
                <MenuItem value="urgent">Urgente</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Descrição do problema"
                variant="outlined"
                margin="dense"
                value={newTicket.description}
                onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNewModal(false)}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={handleCreateTicket}>
            Abrir Ticket
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Detalhe do Ticket */}
      <Dialog open={openDetailModal} onClose={() => setOpenDetailModal(false)} maxWidth="md" fullWidth>
        {selectedTicket && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <span>#{selectedTicket.id} - {selectedTicket.subject}</span>
                <Chip
                  size="small"
                  label={statusLabels[selectedTicket.status]}
                  style={{ background: statusColors[selectedTicket.status], color: "#fff" }}
                />
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box mb={2}>
                <Typography variant="caption" color="textSecondary">
                  Categoria: {categoryLabels[selectedTicket.category]} | 
                  Prioridade: {priorityLabels[selectedTicket.priority]} | 
                  Aberto em: {format(parseISO(selectedTicket.createdAt), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                  {selectedTicket.assignedUser && ` | Responsável: ${selectedTicket.assignedUser.name}`}
                </Typography>
              </Box>

              <div className={classes.chatContainer}>
                {selectedTicket.messages?.map((msg) => (
                  <div
                    key={msg.id}
                    className={`${classes.messageBubble} ${msg.isFromAdmin ? classes.adminMessage : classes.myMessage}`}
                  >
                    <Typography variant="caption" color="textSecondary">
                      {msg.user?.name} - {format(parseISO(msg.createdAt), "dd/MM HH:mm")}
                    </Typography>
                    <Typography variant="body2">{msg.body}</Typography>
                  </div>
                ))}
              </div>

              {selectedTicket.status !== "closed" && (
                <div className={classes.messageInput}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="Digite sua mensagem..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
                  />
                  <IconButton color="primary" onClick={handleSendMessage}>
                    <Send />
                  </IconButton>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              {selectedTicket.status !== "closed" && (
                <Button color="secondary" onClick={handleCloseTicket}>
                  Fechar Ticket
                </Button>
              )}
              <Button onClick={() => setOpenDetailModal(false)}>Fechar</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </MainContainer>
  );
};

export default Support;
